import type {GridFilterItem, GridRowId, GridRowTreeConfig} from "@mui/x-data-grid-premium";
import {GRID_ROOT_GROUP_ID} from "@mui/x-data-grid-premium";

export function muiXItemMatches(item: GridFilterItem | null | undefined, key: Partial<GridFilterItem>): boolean {
    if (!item) return false;
    return Object.entries(key).every(([k, v]) => item[k as keyof GridFilterItem] === v);
}

export function unsureMuixFilterValue(items: GridFilterItem[], key: GridFilterItem, value: GridFilterItem['value'], insertId: GridFilterItem['id']): void {
    const itemIndex = items.findIndex(i => muiXItemMatches(i, key))
    if (itemIndex !== -1) {
        // Do not set value here, would cause mobx's observable modified out of action
        items[itemIndex] = {...items[itemIndex], value};
    } else {
        items.push({
            id: insertId,
            ...key,
            value,
        })
    }
}

export function removeMuixFilterItem(items: GridFilterItem[], key: GridFilterItem): void {
    const itemIndex = items.findIndex(i => muiXItemMatches(i, key))
    if (itemIndex !== -1) {
        items.splice(itemIndex, 1);
    }
}

export function toggleMuixFilterItem(items: GridFilterItem[], key: GridFilterItem, value: GridFilterItem['value'], insertId: GridFilterItem['id']): boolean {
    const itemIndex = items.findIndex(i => muiXItemMatches(i, key))
    if (itemIndex !== -1) {
        items.splice(itemIndex, 1);
        return false;
    } else {
        // Add it
        items.push({
            id: insertId,
            ...key,
            value,
        })
        return true;
    }
}

export function setMuixFilterItem(items: GridFilterItem[], key: Partial<GridFilterItem>, isSet: boolean, ...add: GridFilterItem[]): void {
    const matchIndices = items
        .map((i, idx) => [idx, muiXItemMatches(i, key)] as const)
        .filter(([, matches]) => matches)
        .map(([idx]) => idx);
    if (matchIndices.length > 0) {
        // Remove all matching
        for (let i = matchIndices.length - 1; i >= 0; i--) {
            items.splice(matchIndices[i], 1);
        }
        if (isSet) {
            // Add the new ones at the position of the first removed
            items.splice(matchIndices[0], 0, ...add);
        }
    } else if (isSet) {
        items.push(...add);
    }
}

export function getMuixTreeParents<K extends GridRowId>(tree: GridRowTreeConfig, nodes: Iterable<K>): Set<K> {
    const parents = new Set<K>();
    for (const nodeId of nodes) {
        // add all parent groups
        let node = tree[nodeId];
        while (node.parent && node.parent !== GRID_ROOT_GROUP_ID) {
            const p = node.parent as unknown as K;
            if (parents.has(p)) {
                break;
            }
            parents.add(p)
            node = tree[node.parent]
        }
    }
    return parents;
}

/**
 * Get all children of a tree node that match the filter
 */
export function getMuixTreeChildren<K extends GridRowId>(tree: GridRowTreeConfig, filter: (rowId: K) => boolean, thisId: K | null = null): K[] {
    if (thisId === null) {
        thisId = GRID_ROOT_GROUP_ID as K;
    }
    const node = tree[thisId]
    if (node.type === 'leaf') {
        return [thisId];
    }
    if (node.type === 'group') {
        return node.children.filter(rowId => filter(rowId as K)).flatMap(rowId => getMuixTreeChildren(tree, filter, rowId as K));
    }
    return [];
}
